import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { FormProvider, useForm } from "react-hook-form";
import { TLabelModalDashboard, TModalDashboard } from "..";
import { TConfigBancariaDefault } from "../../../../../../types/api/geral/configuracoes";
import { TConfigBancariasSemIntegracao, TConfiguracaoBancaria } from "../../../../../../types/api/financeiro/configuracoes-bancarias";

export default function DialogEditConfigFinanceiro({
    open,
    handleClose,
    defaultValueModal,
    label,
    modal,
    valorConfigDefault,
    dataConfigBancarias
}: DialogEditConfigFinanceiroProps) {

    const queryClient = useQueryClient();

    const methods = useForm<TFormConfigFinanceiro>()
    const { setValue, watch, handleSubmit, formState: { errors } } = methods;

    //Querys e mutations
    const dataConfiguracoesBancarias: TConfiguracaoBancaria[] = useMemo(() => dataConfigBancarias || [], [dataConfigBancarias])
    const { mutate: atualizarConfig, isLoading } = useMutation({
        mutationKey: 'atualizar-config-bancaria',
        mutationFn: (inputs: TFormEnvioConfig) => axiosClient.post(`atualizar-configuracoes`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao realizar configuração!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["configuracoes"])
            queryClient.invalidateQueries(["pesquisar-configuracoes-bancarias"])
            handleClose()
            enqueueSnackbar('Atualização da configuração realizada com sucesso!', { variant: 'success' });
        }
    })

    //Funções manipuladoras
    const onSubmit = (data: TFormConfigFinanceiro) => {
        const params: TFormEnvioConfig = {
            chave: 'config_bancaria_default',
            valor: {
                emissao_pix: data.valor.emissao_pix,
                emissao_boleto: data.valor.emissao_boleto,
            }
        }
        atualizarConfig(params)
    }

    const handleChangeSelect = (prop: TModalDashboard, id: string | number) => {
        setValue(`${prop}`, id)
        setValue('tipo_emissao', prop)
        const foundIntegracao = dataConfiguracoesBancarias.find((item) => item.id === id);
        if (foundIntegracao) {
            const { integracao, ...rest } = foundIntegracao;
            setValue(`valor.${prop}`, rest)
        }
    }
    //

    useEffect(() => {
        if (dataConfigBancarias) {
            setValue(`${modal}`, defaultValueModal.id)
            setValue('tipo_emissao', modal)
            setValue('valor', valorConfigDefault)
        }
    }, [dataConfigBancarias, modal, defaultValueModal, setValue, valorConfigDefault])

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Config. padrão para {label}
                </DialogTitle>

                {
                    dataConfiguracoesBancarias &&
                    <>
                        <Box
                            component={'form'}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 12}}>
                                        <FormControl fullWidth>
                                            <InputLabel id="bancos-select">Integração</InputLabel>
                                            <Select
                                                labelId="bancos-select"
                                                defaultValue={defaultValueModal.id}
                                                value={watch(`${modal}`)}
                                                label="Integração"
                                                onChange={(e) => handleChangeSelect(modal, e.target.value)}
                                            >
                                                {
                                                    dataConfiguracoesBancarias.length <= 0 &&
                                                    <MenuItem disabled>
                                                        Nenhuma integração disponível
                                                    </MenuItem>
                                                }
                                                {
                                                    dataConfiguracoesBancarias.length > 0 && dataConfiguracoesBancarias.map((item, index) => {
                                                        return (
                                                            <MenuItem key={index} value={item.id}>
                                                                {`${item.nome_banco} - Ag: ${item.agencia}${item.agencia_digito ? `-${item.agencia_digito}` : ''} / Conta: 
                                                                ${item.conta}${item.conta_digito ? `-${item.conta_digito}` : ''}`}
                                                            </MenuItem>
                                                        );
                                                    })
                                                }
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                </Grid>
                                <Box mt={2} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                                    <Button
                                        onClick={handleClose}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        autoFocus
                                        disabled={isLoading}
                                    >
                                        {
                                            (isLoading) &&
                                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                        }
                                        Atualizar
                                    </Button>
                                </Box>
                            </DialogContent>
                        </Box>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}

interface DialogEditConfigFinanceiroProps {
    open: boolean;
    handleClose: () => void;
    label: TLabelModalDashboard
    modal: TModalDashboard
    defaultValueModal: TConfigBancariasSemIntegracao;
    valorConfigDefault: TConfigBancariaDefault
    dataConfigBancarias: TConfiguracaoBancaria[]
}

type TFormConfigFinanceiro = {
    emissao_boleto: string | number,
    emissao_pix: string | number,
    valor: TConfigBancariaDefault
    tipo_emissao: TModalDashboard
    movimentacao_desconto_tipo: string
}

export type TFormEnvioConfig = {
    chave: string;
    valor: TConfigBancariaDefault
}

